import { Box, Menu, MenuItem } from '@material-ui/core';
import ArrowDropDown from '@material-ui/icons/ArrowDropDown';
import { Dispatch } from '@reduxjs/toolkit';
import { DialogVariantEnum } from 'enums/dialogVariant';
import React, { Component, Fragment, MouseEvent } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { AppDispatch, AppState } from 'store';
import { dialogOpen } from 'store/dialog/dialog.actions';
import { IDialogOpenPayload } from 'store/dialog/dialog.interface';
import { IProjectState, ProjectCheckArray } from 'store/projects/projects.interface';
import { getCheckProjectArraySelector, getProjectsSelector } from 'store/projects/projects.selectors';
import { getRequiredJobStatuses } from 'utils/getRequiredJobStatuses';
import { XTMJobStatus } from 'utils/restApplicationClient';
import { BulkActionMenuButton } from './BulkActionsButton.styled';

interface IState {
  anchorEl: Element | null;
}

interface IProps {
  disabled?: boolean;
}

interface IStateProps {
  projectCheckArray: ProjectCheckArray;
  projects: IProjectState;
}

interface IDispatchProps {
  dialogOpen: (payload: IDialogOpenPayload) => AppDispatch;
}

type PropType = WithTranslation & IDispatchProps & IProps & IStateProps;

class BulkActionsButton extends Component<PropType, IState> {
  constructor(props: PropType) {
    super(props);
    this.state = {
      anchorEl: null,
    };
  }

  handleClick = (event: MouseEvent): void => {
    this.setState({ anchorEl: event.currentTarget });
  };

  handleClose = (): void => {
    this.setState({ anchorEl: null });
  };

  handleMenuClick = (variant: DialogVariantEnum) => (): void => {
    const { dialogOpen } = this.props;
    this.handleClose();

    const checkedJobs = this.filterCheckedProjects();

    if (!this.checkIfStatusIsViable(variant, checkedJobs)) {
      dialogOpen({
        variant: DialogVariantEnum.NO_STATUS,
        action: DialogVariantEnum.NO_STATUS,
      });
      return;
    }

    dialogOpen({
      variant,
      action: variant,
      projectInfo: {
        jobs: checkedJobs,
      },
    });
  };

  checkIfStatusIsViable = (variant: DialogVariantEnum, jobs: Array<{ id: string; status: XTMJobStatus }>): boolean => {
    const requiredStatuses = getRequiredJobStatuses(variant);

    return jobs.findIndex(({ status }) => requiredStatuses.includes(status)) >= 0;
  };

  filterCheckedProjects = (): Array<{ id: string; status: XTMJobStatus }> => {
    const {
      projectCheckArray: { projects, checked, indeterminate },
      projects: { data },
    } = this.props;

    const checkedJobs: Array<{ id: string; status: XTMJobStatus }> = [];

    if (data?.length && (checked || indeterminate)) {
      Object.values(projects).forEach((project) => {
        if (project.checked || project.indeterminate) {
          Object.values(project.languages).forEach((language) => {
            const allFiles = Object.entries(language.files).map(([id, { status, checked }]) => ({
              id,
              status,
              checked,
            }));

            if (language.checked) {
              checkedJobs.push(...allFiles);
            } else if (language.indeterminate) {
              const checkedFiles = allFiles.filter(({ checked }) => checked);

              checkedJobs.push(...checkedFiles);
            }
          });
        }
      });
    }

    return checkedJobs;
  };

  render(): JSX.Element {
    const { anchorEl } = this.state;
    const {
      t,
      disabled,
      projectCheckArray: { indeterminate, checked },
    } = this.props;
    const isDisabled = disabled || !(indeterminate || checked);

    return (
      <Fragment>
        <BulkActionMenuButton
          aria-controls="simple-menu"
          aria-haspopup="true"
          onClick={this.handleClick}
          opened={Number(!!anchorEl)}
          disabled={isDisabled}
        >
          <Box fontSize="14px" fontWeight="500">
            {t('queue.actions')}
          </Box>
          <ArrowDropDown fontSize="default" />
        </BulkActionMenuButton>

        <Menu
          id="simple-menu"
          anchorEl={anchorEl}
          keepMounted
          open={!!anchorEl}
          onClose={this.handleClose}
          getContentAnchorEl={null}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          <MenuItem onClick={this.handleMenuClick(DialogVariantEnum.IMPORT)}>{t('queue.labels.IMPORT')}</MenuItem>
          <MenuItem onClick={this.handleMenuClick(DialogVariantEnum.CANCEL)}>{t('queue.labels.CANCEL')}</MenuItem>
          <MenuItem onClick={this.handleMenuClick(DialogVariantEnum.DELETE)}>{t('queue.labels.DELETE')}</MenuItem>
        </Menu>
      </Fragment>
    );
  }
}

const mapStateToProps = (state: AppState): IStateProps => ({
  projectCheckArray: getCheckProjectArraySelector(state),
  projects: getProjectsSelector(state),
});

const mapDispatchToProps = (dispatch: Dispatch<AppDispatch>): IDispatchProps => ({
  dialogOpen: (payload: IDialogOpenPayload): AppDispatch => dispatch(dialogOpen(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(BulkActionsButton));
